import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../App.jsx';
import api from '../services/api.js';
import { APP_VERSION } from '../version.js';
import NavBar from '../components/NavBar.jsx';

const RANK_STYLE = {
  1: { bg: 'linear-gradient(135deg, #f2c063, #ffaa00)', color: '#5a3e00', emoji: '🥇' },
  2: { bg: 'linear-gradient(135deg, #C0C0C0, #a0a0a0)', color: '#333', emoji: '🥈' },
  3: { bg: 'linear-gradient(135deg, #cd7f32, #a0601a)', color: '#fff', emoji: '🥉' },
};

const AVATARS = {
  astronaut: '🧑‍🚀', scientist: '🧑‍🔬', explorer: '🧭', wizard: '🧙',
  robot: '🤖', alien: '👽', dragon: '🐉', unicorn: '🦄',
  ninja: '🥷', pirate: '🏴‍☠️', parent: '👨‍👩‍👧',
};

export default function Ranking() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getWeeklyRanking()
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-screen"><div className="loading-rocket">🏆</div><p>Chargement du classement...</p></div>;
  if (!data) return <div className="loading-screen"><p>Erreur</p><button className="btn btn-primary" onClick={() => navigate('/')}>Retour</button></div>;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <NavBar />

      <div className="container">
        <button className="back-btn" onClick={() => navigate('/')}>← Retour</button>

        <div className="ranking-header">
          <h2 className="ranking-title">🏆 Classement</h2>
          <p className="ranking-week">{data.week_label}</p>
          {data.my_rank && (
            <motion.div className="ranking-my-stats"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', damping: 12 }}>
              <div className="ranking-my-rank">#{data.my_rank}</div>
              <div className="ranking-my-points">{data.my_points} pts</div>
              <div className="ranking-my-tier">{data.my_tier}</div>
            </motion.div>
          )}
        </div>

        {data.leaderboard.length === 0 ? (
          <div className="cards-empty">
            <div className="emoji">🏆</div>
            <h3>Pas encore de joueurs au classement</h3>
            <p style={{ marginTop: 8, color: 'var(--text-muted)' }}>Lance un quiz pour être le premier !</p>
            <button className="btn btn-primary" style={{ maxWidth: 300, margin: '24px auto 0' }}
              onClick={() => navigate('/')}>🚀 Jouer</button>
          </div>
        ) : (
          <div className="ranking-list">
            {data.leaderboard.map((entry, i) => {
              const style = RANK_STYLE[entry.rank] || {};
              const isMe = entry.is_current_user;
              return (
                <motion.div key={entry.username || i}
                  className={`ranking-row ${isMe ? 'me' : ''} ${entry.rank <= 3 ? 'podium' : ''}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}>
                  <div className="ranking-rank" style={entry.rank <= 3 ? { background: style.bg, color: style.color } : {}}>
                    {entry.rank <= 3 ? style.emoji : `#${entry.rank}`}
                  </div>
                  <div className="ranking-avatar">{entry.avatar || '🧑‍🚀'}</div>
                  <div className="ranking-info">
                    <span className="ranking-name">{entry.username}{isMe ? ' (toi)' : ''}</span>
                  </div>
                  <div className="ranking-points-col">
                    <span className="ranking-points">{entry.points}</span>
                    <span className="ranking-pts-label">pts</span>
                  </div>
                  <div className="ranking-tier">{entry.tier}</div>
                </motion.div>
              );
            })}
          </div>
        )}

        <div className="ranking-legend">
          <h4>Comment gagner des points ?</h4>
          <div className="redemption-highlight" style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'linear-gradient(90deg, rgba(52,224,196,0.18), rgba(52,224,196,0.05))',
            border: '1px solid rgba(52,224,196,0.35)', borderRadius: 12,
            padding: '12px 14px', margin: '8px 0 14px',
          }}>
            <span style={{ fontSize: '1.6rem' }}>💪</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>Réussir une question ratée avant</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                Apprendre de ses erreurs, c'est la plus belle victoire !
              </div>
            </div>
            <span style={{ fontWeight: 800, color: 'var(--gold)', whiteSpace: 'nowrap' }}>+150 pts</span>
          </div>
          <div className="ranking-legend-grid">
            <span>✅ Bonne réponse</span><span>+10 pts</span>
            <span>🔥 Combo x2</span><span>+5 pts</span>
            <span>🔥🔥 Combo x3-4</span><span>+10 pts</span>
            <span>🔥🔥🔥 Combo x5+</span><span>+20 pts</span>
            <span>📚 Nouvelle fiche</span><span>+5 pts</span>
            <span>🔗 Connexion</span><span>+15 pts</span>
            <span>🏁 Quiz terminé</span><span>+10 pts</span>
            <span>👑 Quiz parfait</span><span>+50 pts</span>
          </div>
          <p className="ranking-reset-info">⭐ Tes points se cumulent — le classement ne repart jamais à zéro !</p>
        </div>
      </div>
    </motion.div>
  );
}
